import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import Loading from "components/Loading";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { db } from "services/firebase";
import {
  listInstitutionReadinessRows,
  listOrganizationsForRegionalScope,
  readinessStripLabel,
} from "services/fde/readinessService";

const GlobalReadinessPage = () => {
  const { isGlobalAdmin, isRegionalAdmin, userData } = useAuth();
  const [rows, setRows] = useState([]);
  const [orgNames, setOrgNames] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const regionId =
    userData?.regionId || userData?.region || userData?.geographicRegion || null;

  const canAccess = isGlobalAdmin || isRegionalAdmin;

  const load = useCallback(async () => {
    if (!canAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErrorMessage("");
    try {
      let institutionIds = null;
      if (isRegionalAdmin && !isGlobalAdmin) {
        const orgs = await listOrganizationsForRegionalScope(regionId);
        institutionIds = orgs.map((org) => org.id);
        setOrgNames(Object.fromEntries(orgs.map((org) => [org.id, org.name || org.id])));
      } else {
        const orgSnap = await db.collection("organizations").limit(200).get();
        const orgs = orgSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setOrgNames(Object.fromEntries(orgs.map((org) => [org.id, org.name || org.id])));
      }

      const readinessRows = await listInstitutionReadinessRows({ institutionIds });
      setRows(readinessRows);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to load global readiness.");
    } finally {
      setLoading(false);
    }
  }, [canAccess, isGlobalAdmin, isRegionalAdmin, regionId]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    const sorted = [...rows].sort(
      (a, b) => (b.overallReadiness ?? 0) - (a.overallReadiness ?? 0),
    );
    if (statusFilter === "all") return sorted;
    return sorted.filter((row) => {
      const tcis = Object.values(row.tciReadiness || {});
      if (statusFilter === "at-risk") return (row.overallReadiness ?? 0) < 50;
      if (statusFilter === "loop-stalled") {
        return tcis.some((tci) => tci.loopOperating === false);
      }
      return tcis.some((tci) => tci.status === statusFilter);
    });
  }, [rows, statusFilter]);

  if (loading) return <Loading page />;

  if (!canAccess) {
    return <Alert severity="warning">Global or regional admin access required.</Alert>;
  }

  return (
    <Box>
      <DashboardPageHeader
        title="Network readiness"
        subtitle={
          isGlobalAdmin
            ? "Readiness across all institutions."
            : `Institutions in region ${regionId || "unknown"}.`
        }
      />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="readiness-filter-label">Filter</InputLabel>
          <Select
            labelId="readiness-filter-label"
            label="Filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <MenuItem value="all">All institutions</MenuItem>
            <MenuItem value="at-risk">At risk (&lt;50%)</MenuItem>
            <MenuItem value="loop-stalled">Loop stalled</MenuItem>
            <MenuItem value="active">Active TCI gap</MenuItem>
            <MenuItem value="configuring">Configuring</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Institution</TableCell>
              <TableCell align="right">Overall</TableCell>
              <TableCell>TCIs tracked</TableCell>
              <TableCell>Loop gaps</TableCell>
              <TableCell>Quiet Clinical Annotators</TableCell>
              <TableCell>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row) => {
              const tcis = Object.values(row.tciReadiness || {});
              const stalled = tcis.filter((tci) => !tci.loopOperating).length;
              const quiet = tcis.reduce((sum, tci) => sum + (tci.fdecQuietCount || 0), 0);
              return (
                <TableRow key={row.id}>
                  <TableCell>{orgNames[row.institutionId] || row.institutionId}</TableCell>
                  <TableCell align="right">
                    <Chip
                      size="small"
                      color={
                        (row.overallReadiness ?? 0) >= 80
                          ? "success"
                          : (row.overallReadiness ?? 0) >= 50
                            ? "warning"
                            : "error"
                      }
                      label={`${row.overallReadiness ?? 0}% · ${readinessStripLabel(row.overallReadiness ?? 0)}`}
                    />
                  </TableCell>
                  <TableCell>{row.tciCount ?? tcis.length}</TableCell>
                  <TableCell>{stalled}</TableCell>
                  <TableCell>{quiet}</TableCell>
                  <TableCell>
                    {row.lastUpdatedDate
                      ? formatDistanceToNow(row.lastUpdatedDate, { addSuffix: true })
                      : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
            {!filteredRows.length && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" variant="body2">
                    No readiness documents match this filter.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default GlobalReadinessPage;
